import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { FiUsers, FiPlus, FiLogIn } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';

const Classrooms = () => {
  const { isAdmin, isStudent } = useAuth();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joining, setJoining] = useState(false);

  const { data: classrooms, isLoading, refetch } = useQuery(
    {queryKey:['classrooms'],
    queryFn:() => axios.get('/api/classrooms').then(res => res.data.classrooms)}
  );

  const handleJoinClassroom = async (e) => {
    e.preventDefault();
    if (joinCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setJoining(true);
    try {
      await axios.post('/api/classrooms/join', { code: joinCode });
      toast.success('Joined classroom successfully!');
      setShowJoinModal(false);
      setJoinCode('');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to join classroom');
    } finally {
      setJoining(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          {isAdmin ? 'My Classrooms' : 'Joined Classrooms'}
        </h1>
        {isAdmin ? (
          <Link to="/classrooms/create" className="btn-primary">
            <FiPlus className="inline mr-2" />
            Create Classroom
          </Link>
        ) : (
          <button
            onClick={() => setShowJoinModal(true)}
            className="btn-primary"
          >
            <FiLogIn className="inline mr-2" />
            Join Classroom
          </button>
        )}
      </div>

      {classrooms?.length === 0 ? (
        <div className="card text-center py-12">
          <FiUsers className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-lg text-gray-600">
            {isAdmin 
              ? "You haven't created any classrooms yet"
              : "You haven't joined any classrooms yet"}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {isAdmin
              ? "Create your first classroom to get started"
              : "Join a classroom using the code provided by your teacher"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classrooms.map((classroom) => (
              <Link
                key={classroom._id}
                to={`/classrooms/${classroom._id}`}
                className="card hover:shadow-lg transition-shadow"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {classroom.name}
                </h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Code: <span className="font-mono font-bold">{classroom.code}</span></p>
                  {isAdmin && (
                    <p>{classroom.students.length} students enrolled</p>
                  )}
                  {isStudent && (
                    <>
                      <p>Teacher: {classroom.admin.name}</p>
                      {classroom.attemptedQuizCount !== undefined && (
                        <p>{classroom.attemptedQuizCount}/{classroom.quizzes.length} quizzes completed</p>
                      )}
                    </>
                  )}
                  <p>{classroom.quizzes.length} quizzes assigned</p>
                </div>
                <div className="mt-4 text-primary-600 text-sm font-medium">
                  View Details →
                </div>
              </Link>
          ))}
        </div>
      )}

      {/* Join Classroom Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Join Classroom
            </h2>
            <form onSubmit={handleJoinClassroom}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter 6-digit classroom code
              </label>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                className="input-field mb-4"
                placeholder="123456"
                maxLength={6}
                pattern="\d{6}"
                required
              />
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowJoinModal(false);
                    setJoinCode('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={joining}
                  className="btn-primary"
                >
                  {joining ? <LoadingSpinner size="small" /> : 'Join'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Classrooms;